import {
  organizationListVariants,
  organizationScrollAreaVariants,
  organizationSearchWrapperVariants,
  organizationSectionLabelVariants,
  organizationTreeChevronBoxVariants,
  organizationTreeMarkVariants,
  organizationTreeNameVariants,
  organizationTreeRowVariants,
} from '@/features/organization-project-picker/config/organizationTree';
import { useOrganizations } from '@/features/organization-project-picker/services/organizationProjectQueries';
import { supervisorCamerasScopeSearch } from '@/features/supervisor/lib/camerasScopeSearch';
import type { SupervisorCamerasSearch } from '@/features/supervisor/types/supervisor';
import { NavSearch } from '@/shared/components/AppSidebar/NavSearch';
import { NavSectionLabel } from '@/shared/components/AppSidebar/NavSectionLabel';
import { SidebarListSkeleton } from '@/shared/components/AppSidebar/SidebarListSkeleton';
import { ListFailed, ListNextPageFooter } from '@/shared/components/ListStatus';
import { NavEmpty } from '@/shared/components/ui/empty';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useVirtualScrollMargin } from '@/shared/hooks/useVirtualScrollMargin';
import { flattenInfiniteItems, shouldFetchNextVirtualPage } from '@/shared/lib/infiniteQuery';
import { orgInitials } from '@/shared/lib/orgInitials';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Building2 } from 'lucide-react';
import { useCallback, useDeferredValue, useMemo, useRef, useState } from 'react';

/** Organization-only filter list owned by the Cameras rail. */
export function SupervisorCamerasOrganizationList({ search }: { search: SupervisorCamerasSearch }) {
  const { t } = useLingui();
  const activeOrganizationId =
    search.organizationId?.length === 1 ? search.organizationId[0] : undefined;
  const [query, setQuery] = useState('');
  const term = useDebouncedValue(query.trim(), 300);
  const deferredTerm = useDeferredValue(term);
  const organizationsQuery = useOrganizations(deferredTerm || undefined);
  const {
    data: organizationPages,
    isPending: organizationsPending,
    isError: organizationsError,
    error: organizationsQueryError,
    refetch: refetchOrganizations,
    isPlaceholderData,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = organizationsQuery;
  const organizations = useMemo(() => flattenInfiniteItems(organizationPages), [organizationPages]);
  const scrollRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const scrollMargin = useVirtualScrollMargin(scrollRef, listRef, organizations.length);
  const getOrganizationKey = useCallback(
    (index: number) => organizations[index]?.organizationId ?? index,
    [organizations],
  );
  const virtualizer = useVirtualizer<HTMLElement, HTMLLIElement>({
    count: organizations.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: getOrganizationKey,
    estimateSize: () => 32,
    initialRect: { width: 280, height: 640 },
    onChange: (instance, isScrolling) => {
      if (
        shouldFetchNextVirtualPage(
          instance,
          isScrolling,
          organizations.length,
          isPlaceholderData ? false : hasNextPage,
          isFetchingNextPage,
          isFetchNextPageError,
        )
      ) {
        void fetchNextPage();
      }
    },
    overscan: 6,
    scrollMargin,
  });
  const allOrganizations = !search.organizationId?.length;
  const totalCount = organizationPages?.pages[0]?.totalCount ?? organizations.length;

  return (
    <>
      <div className={organizationSearchWrapperVariants()}>
        <NavSearch
          value={query}
          onChange={setQuery}
          placeholder={t`Search…`}
          ariaLabel={t`Search organizations`}
          size="compact"
        />
      </div>
      <section
        ref={scrollRef}
        aria-label={t`Organizations`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: this is the sole scroll viewport and must accept keyboard scrolling.
        tabIndex={0}
        className={organizationScrollAreaVariants()}
      >
        <div className={organizationSectionLabelVariants()}>
          <NavSectionLabel count={totalCount} size="compact">
            <Trans>Organizations</Trans>
          </NavSectionLabel>
        </div>
        {organizationsPending && !organizationPages ? (
          <SidebarListSkeleton />
        ) : organizationsError && !organizationPages ? (
          <ListFailed error={organizationsQueryError} retry={() => void refetchOrganizations()} />
        ) : (
          <>
            <Link
              from="/supervisor/team/cameras"
              to="/supervisor/team/cameras"
              search={(previous) => supervisorCamerasScopeSearch(previous)}
              replace
              aria-current={allOrganizations ? 'page' : undefined}
              data-active={allOrganizations}
              className={organizationTreeRowVariants({ active: allOrganizations })}
            >
              <span aria-hidden className={organizationTreeChevronBoxVariants()} />
              <span
                aria-hidden
                className={organizationTreeMarkVariants({ active: allOrganizations })}
              >
                <Building2 className="size-3" />
              </span>
              <span className={organizationTreeNameVariants({ active: allOrganizations })}>
                <Trans>All organizations</Trans>
              </span>
            </Link>
            {organizations.length === 0 ? (
              <NavEmpty>
                <Trans>No organizations</Trans>
              </NavEmpty>
            ) : null}
            <ul
              ref={listRef}
              className={organizationListVariants({ searching: term !== deferredTerm })}
              style={{ height: virtualizer.getTotalSize() }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const organization = organizations[virtualRow.index];
                if (!organization) return null;
                const active = activeOrganizationId === organization.organizationId;
                return (
                  <li
                    key={organization.organizationId}
                    ref={virtualizer.measureElement}
                    data-index={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                    }}
                  >
                    <div className={organizationTreeRowVariants({ active })}>
                      <span aria-hidden className={organizationTreeChevronBoxVariants()} />
                      <Link
                        from="/supervisor/team/cameras"
                        to="/supervisor/team/cameras"
                        search={(previous) =>
                          supervisorCamerasScopeSearch(previous, organization.organizationId)
                        }
                        replace
                        aria-current={active ? 'page' : undefined}
                        data-active={active}
                        className="flex min-w-0 flex-1 items-center gap-1 self-stretch rounded-md outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
                      >
                        <span aria-hidden className={organizationTreeMarkVariants({ active })}>
                          {orgInitials(organization.name)}
                        </span>
                        <span className={organizationTreeNameVariants({ active })}>
                          {organization.name}
                        </span>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
            <ListNextPageFooter query={organizationsQuery} />
          </>
        )}
      </section>
    </>
  );
}
